import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseRepository, HttpCancelableRequest } from '../Common/Data/base.repository';
import {
    ActionViewContextOptionResult,
    ActionViewSavedSearch,
    ActionViewSearchRequest,
    ActionViewSearchResult,
    ActionViewTaskTypeOptions,
    ActionViewWelcomeSearchResult,
} from '../Task/ActionView/actionView.models';

export interface ObtainPaymentReceiptTasksRequest {
    taskIDs: number[];
    searchTimestamp: Date;
}

export interface CompleteTrackDataEnterRefundTasksRequest {
    taskIds: number[];
    searchTimestamp: Date;
}

export interface ActionViewCategoryInput {
    categoryId?: number;
    categoryName: string;
    categoryType: Core.SearchCategoryType;
}

export interface ActionViewSavedSearchPatch {
    actionViewID?: number;
    actionViewName?: string;
    categoryID?: number | string;
    categoryId?: number;
    defaultDisplay?: number;
    isShared?: boolean;
    rowVersion?: number[];
}

export interface ActionViewFavoriteInput {
    actionViewID: number;
}

export interface TopLevelCompanyData {
    topLevelCompanyId: number;
    litigationReferralsEnabled: boolean;
}

@Injectable({ providedIn: 'root' })
export class ActionViewRepository extends BaseRepository {
    constructor(httpClient: HttpClient) {
        super(httpClient);
    }

    getTaskTypes(): Observable<ActionViewTaskTypeOptions> {
        return this.httpGet('/api/TaskActionView/TaskTypes');
    }

    search(request: ActionViewSearchRequest): HttpCancelableRequest<ActionViewSearchResult> {
        return this.httpPostWithCancel('/api/TaskActionView/Search', request);
    }

    welcomeSearch(viewMode: number): Observable<ActionViewWelcomeSearchResult> {
        return this.httpGet(`/api/TaskActionView/welcome/${viewMode}`);
    }

    startExport(request: ActionViewSearchRequest): Observable<number> {
        return this.httpPost('/api/TaskActionView/export', request);
    }

    getCategories(): Observable<Core.ActionViewSavedSearchCategoryManagementDTO[]> {
        return this.httpGet('/api/TaskActionView/Category');
    }

    createCategory(category: ActionViewCategoryInput): Observable<Core.SavedSearchCategoryManagementDTO> {
        return this.httpPost('/api/TaskActionView/Category', category);
    }

    reorderCustomCategories(
        categories: Core.ActionViewSavedSearchCategoryManagementDTO[],
    ): Observable<Core.ActionViewSavedSearchCategoryManagementDTO[]> {
        return this.httpPut('/api/TaskActionView/Category/Custom/Reorder', categories);
    }

    getCustomActionView(actionViewId: number): Observable<ActionViewSavedSearch> {
        return this.httpGet(`/api/TaskActionView/Custom/${actionViewId}`);
    }

    getSystemActionView(actionViewId: number): Observable<ActionViewSavedSearch> {
        return this.httpGet(`/api/TaskActionView/System/${actionViewId}`);
    }

    createCustomActionView(actionView: ActionViewSavedSearch): Observable<ActionViewSavedSearch> {
        return this.httpPost('/api/TaskActionView/Custom', actionView);
    }

    createSystemActionView(actionView: ActionViewSavedSearch): Observable<ActionViewSavedSearch> {
        return this.httpPost('/api/TaskActionView/System', actionView);
    }

    updateCustomActionView(actionView: ActionViewSavedSearch): Observable<ActionViewSavedSearch> {
        return this.httpPut('/api/TaskActionView/Custom', actionView);
    }

    updateSystemActionView(actionView: ActionViewSavedSearch): Observable<ActionViewSavedSearch> {
        return this.httpPut('/api/TaskActionView/System', actionView);
    }

    patchCustomActionView(actionViewId: number, patch: ActionViewSavedSearchPatch): Observable<ActionViewSavedSearch> {
        return this.httpPatch(`/api/TaskActionView/Custom/${actionViewId}`, patch);
    }

    patchSystemActionView(actionViewId: number, patch: ActionViewSavedSearchPatch): Observable<ActionViewSavedSearch> {
        return this.httpPatch(`/api/TaskActionView/System/${actionViewId}`, patch);
    }

    deleteCustomActionView(actionView: Core.ActionViewListDTO): Observable<void> {
        return this.httpDelete('/api/TaskActionView/Custom', { body: actionView });
    }

    deleteSystemActionView(actionView: Core.ActionViewListDTO): Observable<void> {
        return this.httpDelete('/api/TaskActionView/System', { body: actionView });
    }

    addFavorite(actionViewId: number): Observable<unknown> {
        const input: ActionViewFavoriteInput = { actionViewID: actionViewId };
        return this.httpPost('/api/TaskActionView/Favorite', input);
    }

    deleteFavorite(actionViewId: number): Observable<void> {
        return this.httpDelete(`/api/TaskActionView/Favorite/${actionViewId}`);
    }

    getFavoriteCount(actionViewId: number): Observable<number> {
        return this.httpGet(`/api/TaskActionView/System/FavoriteCount/${actionViewId}`);
    }

    hasFavorite(actionViewId: number): Observable<boolean> {
        return this.httpGet(`/api/TaskActionView/System/HasFavorite/${actionViewId}`);
    }

    share(actionView: Core.ActionViewListDTO): Observable<void> {
        return this.httpPatch('/api/TaskActionView/Share', actionView);
    }

    revokeShare(actionView: ActionViewSavedSearchPatch): Observable<void> {
        return this.httpPatch('/api/TaskActionView/RevokeShare', actionView);
    }

    getTaskContextOptions(itemPayload: number[]): Observable<ActionViewContextOptionResult> {
        return this.httpPost('/api/TaskActionView/TaskContextOptions', itemPayload);
    }

    addObtainPaymentreceiptTasks(taskIds: number[]) {
        return this.httpPut('api/tasks/AddObtainPaymentReceiptTasks', taskIds);
    }

    addObtainWorkpapersTasks(taskIds: number[]): Observable<unknown> {
        return this.httpPost('api/tasks/AddObtainWorkpapersTasks', taskIds);
    }

    completeObtainPaymentReceiptTasks(payload: ObtainPaymentReceiptTasksRequest): Observable<unknown> {
        return this.httpPut('api/tasks/completeobtainpaymentreceipt', payload);
    }

    completeTrackDataEnterRefundTasks(
        payload: CompleteTrackDataEnterRefundTasksRequest,
    ): Observable<Core.BulkOperationResult[]> {
        return this.httpPost<Core.BulkOperationResult[], CompleteTrackDataEnterRefundTasksRequest>(
            '/api/refund/processing/BulkCompleteDataEnter',
            payload,
        );
    }

    updateClientPaymentApproverApproval(
        paymentId: number,
        request: Core.ClientPaymentApproverUpdateRequestModel,
    ): Observable<Core.ClientPaymentApproverUpdateResponseModel> {
        return this.httpPatch(`/api/payment/${paymentId}/client-approval`, request);
    }

    bulkUpdatePaymentApprover(
        taskIds: number[],
        approverContactId: number,
    ): Observable<Core.PaymentBatchBulkUpdateApproverResultModel> {
        return this.httpPut<
            Core.PaymentBatchBulkUpdateApproverResultModel,
            Core.ActionViewBulkUpdateApproverRequestModel
        >('/api/TaskActionView/payments/approver', { taskIds, approverContactId });
    }

    bulkUpdateClientApprovalByTaskIds(
        taskIds: number[],
        paymentApprovalStatusId: number | null,
        paymentApprovalComment: string | null,
    ): Observable<Core.ClientPaymentApproverBulkUpdateResultModel> {
        return this.httpPost<
            Core.ClientPaymentApproverBulkUpdateResultModel,
            Core.ActionViewClientApprovalRequestModel
        >('/api/TaskActionView/payments/client-approval', { taskIds, paymentApprovalStatusId, paymentApprovalComment });
    }

    bulkResetClientApprovalByTaskIds(taskIds: number[]): Observable<Core.ClientPaymentApproverBulkUpdateResultModel> {
        return this.httpPost<
            Core.ClientPaymentApproverBulkUpdateResultModel,
            Core.ActionViewClientApprovalResetRequestModel
        >('/api/TaskActionView/payments/client-approval/reset', { taskIds });
    }
}
